import { useEffect, useState } from 'react';
import { AuthProvider, useAuth, Login } from '@/features/auth';
import { Dashboard } from '@/features/dashboard';
import { CleanerWorkspace } from '@/features/cleaner';
import { ApprovalWorkspace } from '@/features/approvals';
import { PlanningWorkspace } from '@/features/planning';
import { EconomyWorkspace } from '@/features/economy';
import { NotificationsWorkspace } from '@/features/notifications';
import { QCWorkspace, QCSiteView } from '@/features/qc';
import { Sidebar } from '@/components/Sidebar';
import { Loader2 } from 'lucide-react';
import { type Language, type ViewState } from './types';

const validViews: ViewState[] = ['dashboard', 'brfs', 'calendar', 'extras', 'team', 'approvals', 'qc', 'invoicing', 'salary', 'notifications', 'economy'];

type HashState = {
  view: ViewState;
  qcSiteId: string | null;
};

// Redirect legacy hashes to Economy sub-tabs before parsing.
const LEGACY_REDIRECTS: Record<string, string> = {
  invoicing: '/economy/fakturering',
  salary: '/economy/loner',
  extras: '/economy/extra',
};

function parseHash(): HashState {
  const hash = window.location.hash.replace('#/', '').replace('#', '');
  const pathOnly = hash.split('?')[0];
  const segments = pathOnly.split('/').filter(Boolean);
  const root = segments[0] ?? '';

  // Redirect legacy views to their Economy sub-tab equivalents.
  if (LEGACY_REDIRECTS[root]) {
    window.location.replace(`#${LEGACY_REDIRECTS[root]}`);
    return { view: 'economy', qcSiteId: null };
  }

  const view: ViewState = validViews.includes(root as ViewState)
    ? (root as ViewState)
    : 'dashboard';

  const qcSiteId = view === 'qc' && segments[1] ? segments[1] : null;

  return { view, qcSiteId };
}

function AppShell() {
  const { user, profile, isLoading: authLoading, signOut, hardResetAuth } = useAuth();
  const initialHash = parseHash();
  const [currentView, setCurrentView] = useState<ViewState>(initialHash.view);
  const [selectedQcSiteId, setSelectedQcSiteId] = useState<string | null>(initialHash.qcSiteId);
  const [language, setLanguage] = useState<Language>('sv');
  const isCleaner = profile?.role === 'cleaner';

  useEffect(() => {
    const handleHashChange = () => {
      const next = parseHash();
      setCurrentView(next.view);
      setSelectedQcSiteId(next.qcSiteId);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (isCleaner && currentView !== 'dashboard') {
      window.location.hash = '/dashboard';
    }
  }, [currentView, isCleaner]);

  const handleViewChange = (view: ViewState) => {
    window.location.hash = `/${isCleaner ? 'dashboard' : view}`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  const displayName = profile?.full_name || user.email?.split('@')[0] || 'User';
  const sidebarUser = {
    id: profile?.id ?? user.id,
    name: displayName,
    role: profile?.role ?? 'cleaner',
    email: profile?.email ?? user.email ?? '',
    initials: displayName.substring(0, 2).toUpperCase(),
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar
        currentView={currentView}
        onChangeView={handleViewChange}
        language={language}
        setLanguage={setLanguage}
        user={sidebarUser}
        onLogout={async () => {
          await signOut();
          handleViewChange('dashboard');
        }}
        onEmergencyLogout={hardResetAuth}
      />

      <main className="flex-1 overflow-y-auto relative flex flex-col w-full">
        <div className="p-4 pt-16 md:pt-4 md:p-8 max-w-7xl mx-auto w-full flex-1">
          {isCleaner ? (
            <CleanerWorkspace language={language} />
          ) : currentView === 'approvals' ? (
            <ApprovalWorkspace language={language} />
          ) : currentView === 'economy' ? (
            <EconomyWorkspace language={language} />
          ) : currentView === 'notifications' ? (
            <NotificationsWorkspace language={language} />
          ) : currentView === 'qc' ? (
            selectedQcSiteId ? (
              <QCSiteView
                siteId={selectedQcSiteId}
                language={language}
                onBack={() => {
                  window.location.hash = '/qc';
                }}
              />
            ) : (
              <QCWorkspace
                language={language}
                onSelectSite={(siteId) => {
                  window.location.hash = `/qc/${siteId}`;
                }}
              />
            )
          ) : currentView === 'dashboard' ? (
            <Dashboard language={language} onNavigate={handleViewChange} />
          ) : (
            <PlanningWorkspace language={language} />
          )}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
